import PropTypes from 'prop-types';
import React from 'react';
import { useSelector } from 'react-redux';
import { useFlags } from 'wsm-common-data';
import { addNewRelicPageAction } from '@ddc/new-relic';
import ColorSwatch from './ColorSwatch';
import '../global-sass/main.scss';
import { isHondaOrAcuraProfile } from '../utils/isHondaOrAcuraProfile'; // Ensure this is the correct path
import { EV_QUICK_SPECS, quickSpecIDCLabelMapping } from '../global-constants';
import DetailsLink from './DetailsLink';

// https://stackoverflow.com/questions/2901102/how-to-print-a-number-with-commas-as-thousands-separators-in-javascript
const numberWithCommas = (x) => {
	return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};
const getEvDetailsContent = (
	labels,
	contents,
	isGroupEV = false,
	siteFranchises
) => {
	const defaultSubText = labels.get(
		'EV_QUICKSPECDETAILS_SPEC_SUB_TEXT_VERBIAGE'
	);
	const oemSubText = labels.get(
		'EV_HONDA_ACURA_QUICKSPECDETAILS_SPEC_SUB_TEXT_VERBIAGE'
	);

	const isOEMProfile = isHondaOrAcuraProfile(siteFranchises);

	const subText = isOEMProfile
		? `${defaultSubText}<br><br>${oemSubText}`
		: defaultSubText;

	if (isGroupEV) {
		const evQuickSpecsDetails = contents
			.map(({ key, titleLabel, detailLabel, isPicked }) => {
				return {
					key,
					title: labels.get(titleLabel),
					details: labels.get(detailLabel),
					isPicked
				};
			})
			.filter((item) => item.details);
		return {
			mainDetail: null,
			subContent: evQuickSpecsDetails,
			subText
		};
	}

	return {
		details:
			labels.get(contents) !== contents ? labels.get(contents) : null,
		subText
	};
};

const openEvQuickSpecModal = (labels, onOpenModal, key, evDataContent) => {
	const evDetailsSubContent = evDataContent.subContent.map((evItem) => ({
		...evItem,
		isPicked: evItem.key === key
	}));
	const groupTitleLabel = labels.get('EV_QUICKSPECDETAILS_SPEC_TITLE');
	onOpenModal(
		groupTitleLabel,
		{ ...evDataContent, subContent: evDetailsSubContent },
		true
	);
};

const replaceLabelHolderFn = (labels, enablePlaceholderLabels) => {
	const unValidValues = ['null', 'undefined', 'Unknown'];

	if (!enablePlaceholderLabels) {
		return (value) => value;
	}

	return (value, keyLabel) => {
		if (!value || unValidValues.includes(value)) {
			return null;
		}

		if (!keyLabel) {
			return value;
		}

		const { placeHolderLabel, selector } =
			quickSpecIDCLabelMapping[keyLabel] || {};

		if (!placeHolderLabel) {
			return value;
		}

		if (keyLabel === 'combinedFuelEfficiency') {
			const formatValue = String(value || '').replace(/\D/g, '');
			return labels.get(placeHolderLabel)?.replace(selector, formatValue);
		}

		return labels.get(placeHolderLabel)?.replace(selector, value);
	};
};

const renderSpecs = ({
	quickSpecs,
	labels,
	isElectric,
	onOpenModal,
	useLabelPlaceHolder,
	enableEnhancedStyling,
	siteFranchises
}) => {
	const {
		bodyStyle,
		cityFuelEconomy,
		engine,
		engineSize,
		exteriorColor,
		normalExteriorColor,
		highwayFuelEconomy,
		interiorColor,
		normalInteriorColor,
		maxSeatingCapacity,
		odometer,
		combinedFuelEfficiency,
		horsePower
	} = quickSpecs;

	const replaceLabelHolder = replaceLabelHolderFn(
		labels,
		useLabelPlaceHolder
	);

	// Not showing the odometer field when vehicle type is 'NEW'
	// Verify odometer value exists and reformat
	const odometerValue =
		odometer &&
		`${numberWithCommas(odometer)} ${labels.get('MILES_KILOMETERS')}`;

	const fuelEconomyValue =
		cityFuelEconomy &&
		typeof cityFuelEconomy === 'number' &&
		highwayFuelEconomy &&
		typeof highwayFuelEconomy === 'number' &&
		// remove 0 behind decimal if exist
		`${parseFloat(cityFuelEconomy.toFixed(1))}/${parseFloat(
			highwayFuelEconomy.toFixed(1)
		)} ${useLabelPlaceHolder ? '' : labels.get('MPG_CITY_HWY')}`;

	const horsePowerValue = isElectric ? horsePower : null;

	const extColorSwatch = (
		<ColorSwatch
			normalizedColor={normalExteriorColor}
			fullColor={exteriorColor}
		/>
	);

	const intColorSwatch = (
		<ColorSwatch
			normalizedColor={normalInteriorColor}
			fullColor={interiorColor}
		/>
	);

	let bodySeatingKey;
	let bodySeatingValue;
	if (bodyStyle && maxSeatingCapacity) {
		bodySeatingKey = labels.get('BODY_SEATING');
		bodySeatingValue = `${bodyStyle}/${maxSeatingCapacity} ${labels.get(
			'SEATS'
		)}`;
	} else if (maxSeatingCapacity) {
		// This is a fallback in case bodyStyle is not available
		// but this should not be needed and can potentially be removed for simplicity
		bodySeatingKey = labels.get('SEATING');
		bodySeatingValue = `${maxSeatingCapacity} ${labels.get('SEATS')}`;
	}

	let engineValue;
	if (isElectric) {
		engineValue = null;
	} else if (engineSize && engine) {
		engineValue = `${engineSize} ${engine}`;
	} else if (engine) {
		engineValue = engine;
	} else if (engineSize) {
		engineValue = engineSize;
	}

	const specObj = {};

	const labelsObj = {
		exteriorColor: labels.get('EXTERIOR_COLOR'),
		interiorColor: labels.get('INTERIOR_COLOR'),
		odometer: labels.get('ODOMETER'),
		maxSeatingCapacity: bodySeatingKey,
		year: labels.get('YEAR'),
		trim: labels.get('TRIM'),
		vin: labels.get('VIN'),
		stockNumber: labels.get('STOCK_NUMBER'),
		fuelEconomy: labels.get('FUEL_ECONOMY'),
		transmission: labels.get('TRANSMISSION'),
		modelCode: labels.get('MODEL_CODE'),
		engine: labels.get('ENGINE'),
		driveLine: labels.get('DRIVETRAIN'),
		horsePower: labels.get('HORSE_POWER'),
		model: labels.get('MODEL'),
		doors: labels.get('DOORS'),
		fuelType: labels.get('FUEL_TYPE'),
		cab: labels.get('CAB'),
		make: labels.get('MAKE'),
		electricRange: labels.get('EV_RANGE'),
		totalRange: labels.get('TOTAL_RANGE'),
		combinedFuelEfficiency: labels.get('COMBINED_FUEL_ECONOMY'),
		kWhCombinedFuelEfficiency: labels.get('EV_COMBINED_FUEL_ECONOMY'),
		batteryWarranty: labels.get('EV_BATTERY_WARRANTY'),
		bed: labels.get('TRUCK_BED_LENGTH')
	};
	// Filter out items that included in labelsObj config from quickSpecs
	const specKeys = Object.keys(quickSpecs).filter(
		(attrKey) => !!labelsObj[attrKey]
	);

	specKeys.forEach((attr) => {
		if (!specObj[labelsObj[attr]] && typeof quickSpecs[attr] !== 'object') {
			switch (attr) {
				case 'maxSeatingCapacity':
					specObj[labelsObj[attr]] = replaceLabelHolder(
						bodySeatingValue,
						attr
					);
					break;
				case 'engine':
					specObj[labelsObj[attr]] = replaceLabelHolder(
						engineValue,
						attr
					);
					break;
				case 'fuelEconomy':
					specObj[labelsObj[attr]] = replaceLabelHolder(
						fuelEconomyValue,
						attr
					);
					break;
				case 'odometer':
					specObj[labelsObj[attr]] = replaceLabelHolder(
						odometerValue,
						attr
					);
					break;
				case 'combinedFuelEfficiency':
					specObj[labelsObj[attr]] = replaceLabelHolder(
						combinedFuelEfficiency,
						attr
					);
					break;
				case 'horsePower':
					specObj[labelsObj[attr]] = replaceLabelHolder(
						horsePowerValue?.replace(/hp.*$/i, ''),
						attr
					);
					break;
				default:
					specObj[labelsObj[attr]] = replaceLabelHolder(
						quickSpecs[attr],
						attr
					);
					break;
			}
		}
	});

	const evQuickSpecs = EV_QUICK_SPECS.map((evItem) => {
		if (Object.keys(quickSpecs).includes(evItem.key)) {
			return {
				...evItem,
				value: quickSpecs[evItem.key]?.toString() || '',
				isPicked: false
			};
		}
		return evItem;
	}).filter((item) => item.value);

	const evDetailsData = getEvDetailsContent(
		labels,
		evQuickSpecs,
		true,
		siteFranchises
	);

	const arrayKeys = Object.keys(specObj);

	const itemClassesName = useLabelPlaceHolder
		? 'col-xs-7 px-0 w-100'
		: 'col-xs-7 px-0';

	return arrayKeys
		.filter((key) => (key !== 'FUEL_ECONOMY' ? specObj[key] : true)) // remove keys that don't have a value
		.map((key) => {
			if (key === labels.get('COMBINED_FUEL_ECONOMY') && !isElectric) {
				return null;
			}

			if (key === labels.get('FUEL_ECONOMY')) {
				if (isElectric) {
					return null;
				}
				const fuelDetailContents = getEvDetailsContent(
					labels,
					'EV_MPG_SPEC_DETAILS_VERBIAGE',
					false,
					siteFranchises
				);
				const FuelDetailLabel = labels.get('EV_MPG_SPEC_TITLE');
				const renderDetailLink = fuelDetailContents &&
					fuelDetailContents?.details && (
						<DetailsLink
							onClickDetails={() =>
								onOpenModal(FuelDetailLabel, fuelDetailContents)
							}
						/>
					);
				if (enableEnhancedStyling) {
					return (
						<div key={key} className="ws-quick-specs__spec-pair">
							<dt className="px-0">{key}</dt>
							<dd className="px-0">
								<span
									className="mr-3"
									// eslint-disable-next-line react/no-danger
									dangerouslySetInnerHTML={{
										__html: specObj[key]
									}}
								/>
								{renderDetailLink}
							</dd>
						</div>
					);
				}

				return [
					useLabelPlaceHolder ? null : (
						<dt key={`${key}_name`} className="text-muted col-xs-5">
							<span>{key}</span>
						</dt>
					),
					<dd key={`${key}_val`} className={itemClassesName}>
						<span
							className="mr-3"
							// eslint-disable-next-line react/no-danger
							dangerouslySetInnerHTML={{
								__html: specObj[key]
							}}
						/>
						{renderDetailLink}
					</dd>
				];
			}
			const isColorKey =
				key === labels.get('EXTERIOR_COLOR') ||
				key === labels.get('INTERIOR_COLOR');
			const colorSwatch =
				(key === labels.get('EXTERIOR_COLOR') && extColorSwatch) ||
				(key === labels.get('INTERIOR_COLOR') && intColorSwatch);
			const ddContent = (
				<>
					{isColorKey ? (
						<span className="ws-quick-specs__color-swatch-wrapper">
							<span className="ws-quick-specs__color-swatch-icon">{colorSwatch}</span>
							<span
								// eslint-disable-next-line react/no-danger
								dangerouslySetInnerHTML={{ __html: specObj[key] }}
							/>
						</span>
					) : (
						<span
							className="mr-3"
							// eslint-disable-next-line react/no-danger
							dangerouslySetInnerHTML={{ __html: specObj[key] }}
						/>
					)}
					{evQuickSpecs.map((item) => {
						const itemValueLowerCase =
							item.value?.toLocaleLowerCase();
						const specKeyLowerCase = specObj[key]
							?.toString()
							?.toLocaleLowerCase();

						if (
							specKeyLowerCase.includes(itemValueLowerCase) &&
							key === labels.get('COMBINED_FUEL_ECONOMY')
						) {
							return (
								<DetailsLink
									key={`${item.key}_detailsLink`}
									onClickDetails={() =>
										openEvQuickSpecModal(
											labels,
											onOpenModal,
											item.key,
											evDetailsData
										)
									}
								/>
							);
						}
						return null;
					})}
				</>
			);

			if (enableEnhancedStyling) {
				return (
					<div key={key} className="ws-quick-specs__spec-pair">
						<dt className="px-0">{key}</dt>
						<dd className="px-0">{ddContent}</dd>
					</div>
				);
			}

			return [
				useLabelPlaceHolder ? null : (
					<dt key={`${key}_name`} className="text-muted col-xs-5">
						{key}
					</dt>
				),
				<dd key={`${key}_val`} className={itemClassesName}>
					{ddContent}
				</dd>
			];
		});
};

export default function QuickSpecs({
	quickSpecs,
	deviceType,
	labels,
	isElectric,
	onOpenModal
}) {
	const flags = useFlags();
	const { franchises: siteFranchises } = useSelector(
		(state) => state.siteInfo || {}
	);

	if (
		Object.keys(quickSpecs).length === 0 &&
		quickSpecs.constructor === Object
	) {
		addNewRelicPageAction('ws-quick-specs-render-failure', {
			quickSpecs: 'empty'
		});
		return null;
	}

	const useLabelPlaceHolder = flags['enable-label-placeholder'];
	const enableEnhancedStyling = flags['ws-quick-specs-enhanced-layout'];
	const renderedSpecs = renderSpecs({
		quickSpecs,
		labels,
		isElectric,
		onOpenModal,
		useLabelPlaceHolder,
		enableEnhancedStyling,
		siteFranchises
	}).filter((spec) => spec !== null);
	const useLabelHolderClasses = useLabelPlaceHolder ? 'row mx-0' : 'row';

	if (enableEnhancedStyling) {
		return (
			<div className="ws-quick-specs--enhanced-layout-container">
				<dl className="ws-quick-specs--enhanced-layout">
					{renderedSpecs}
				</dl>
			</div>
		);
	}

	if (deviceType === 'MOBILE' || renderedSpecs.length === 1) {
		return (
			<dl
				className={`line-height-condensed dl-horizontal ${useLabelHolderClasses} list-spaced`}
			>
				{renderedSpecs}
			</dl>
		);
	}

	const middleItemIndex = Math.ceil(renderedSpecs.length / 2);
	const firstColumn = renderedSpecs.slice(0, middleItemIndex);
	const secondColumn = renderedSpecs.slice(middleItemIndex);

	return (
		<div className={useLabelHolderClasses}>
			<div className="col-xs-6">
				<dl className="line-height-condensed dl-horizontal row list-spaced">
					{firstColumn}
				</dl>
			</div>
			<div className="col-xs-6">
				<dl className="line-height-condensed dl-horizontal row list-spaced">
					{secondColumn}
				</dl>
			</div>
		</div>
	);
}

QuickSpecs.propTypes = {
	quickSpecs: PropTypes.shape({}),
	deviceType: PropTypes.string.isRequired,
	labels: PropTypes.shape({
		get: PropTypes.func.isRequired
	}).isRequired,
	isElectric: PropTypes.bool,
	onOpenModal: PropTypes.func
};

QuickSpecs.defaultProps = {
	quickSpecs: {},
	isElectric: false
};
